import {
  IServerSideGetRowsParams,
  IServerSideDatasource,
} from 'ag-grid-enterprise';
import { applyServerSideError, applyServerSideSuccess } from '@ardoq/table';
import { mapAgGridParamsToArdoqFormat } from './mapAgGridParamsToArdoqFormat';
import {
  FetchNextRowsBatchFunction,
  InventoryContext,
  NextRowsBatchResult,
} from './types';
import { mergeScopeDataIntoInventoryContext } from './mergeScopeDataIntoInventoryContext';
import { DEFAULT_ARDOQ_I18NLOCALE, I18nLocale } from '@ardoq/i18n';

export const getServerSideDatasource = (
  getNextRowsBatch: FetchNextRowsBatchFunction
): IServerSideDatasource => ({
  getRows: async params => {
    const getNextRowsBatchParams = mapAgGridParamsToArdoqFormat(params);
    const nextRowsBatchOrError = await getNextRowsBatch(getNextRowsBatchParams);
    return applyNextRowsBatchResultInAgGrid(params, nextRowsBatchOrError);
  },
});

const getI18nLocale = (params: IServerSideGetRowsParams): I18nLocale => {
  const context = params.api.getGridOption('context') as
    | InventoryContext
    | undefined;
  return context?.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;
};

export const applyNextRowsBatchResultInAgGrid = (
  params: IServerSideGetRowsParams,
  nextRowsBatchOrError: NextRowsBatchResult
) => {
  if (nextRowsBatchOrError.status === 'error') {
    applyServerSideError({
      params,
      error: nextRowsBatchOrError.error,
      i18nLocale: getI18nLocale(params),
    });
    return;
  }

  const { rows, totalNumberOfRows, apiScopeData } = nextRowsBatchOrError.result;
  applyServerSideSuccess({
    params,
    rows,
    total: totalNumberOfRows,
    // Show the empty state only when there are no rows AND no pinned top rows
    // (create mode).
    shouldShowNoRowsOverlay: () => {
      const hasPinnedTopRows =
        (params.api.getGridOption('pinnedTopRowData') ?? []).length > 0;
      return totalNumberOfRows === 0 && !hasPinnedTopRows;
    },
  });

  /**
   * Initial scopeData is from the request for columns. It contains all models and workspaces,
   * whereas the subsequent requests (with component entries) contain only models and workspaces depending on the components.
   * They include workspaces and models for the connected components - the once that are referenced from the components in the table.
   * We don't want to include them for example in the dropdowns for component type selection or workspace selection when creating new components.
   */
  mergeScopeDataIntoInventoryContext(params.api, apiScopeData);
};
